// @catalog F.030
/**
 * MarginsLevel1 — orchestrator de /margenes (plan dashboard_executive_redesign).
 * Preserva INTACTO el check de useMarginsPermissions al inicio.
 *
 * Vistas y helpers extraídos a ./Margenes/ para respetar P7 (≤500 líneas):
 * MargenesViews, StoreMarginDrillPanel, margenes-format.
 *
 * period + filters en useState (NO se persisten): /margenes arranca SIEMPRE en el
 * mes en curso (defaultPeriod="mtd"). El filtro es por MES (granularity="month",
 * igual que /ventas) y afecta toda la data del dashboard vía el query
 * (startMonth/endMonth → useMarginsLevel1Metrics). MarginsService.formatCurrency (MXN).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Collapse,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  useMarginsLevel1Metrics,
  useStoreMarginGrowth,
} from "../hooks/useMargins";
import { useMarginsPermissions } from "../hooks/useMarginsPermissions";
import { useLocalStorageState } from "../hooks/useLocalStorageState";
import { useProductDetailDialog } from "../hooks/useProductDetailDialog";
import { ProductDetailDrawer } from "./Forecast/product-detail/ProductDetailDrawer";
import {
  FilterBar,
  type DateRange,
  type StoreOption,
} from "./Dashboard/FilterBar";
import { KPICardsMargenes } from "./Margenes/KPICards";
import MarginsService, {
  type StoreMarginMetrics,
  type TopMarginProduct,
} from "../services/margins.service";
import { PageHead } from "./analytics/PageHead";
import {
  PeriodSelector,
  type PeriodKey,
  type PeriodValue,
  periodToDateRange,
} from "./analytics/PeriodSelector";
import {
  CompareSelector,
  type CompareKey,
} from "./analytics/CompareSelector";
import { AlertBanner } from "./analytics/AlertBanner";
import { SubnavTabs } from "./analytics/SubnavTabs";
import { type ScatterProduct } from "./analytics/MarginDistribution";
import {
  MONTH_LABELS_FULL,
  aggregatePeriodMargins,
  buildMarginRankItems,
  clampToCurrentMonth,
  defaultPeriod,
  isoToMonthKey,
  monthOrder,
  pickCurrent,
} from "./Margenes/margenes-format";
import {
  CriticosView,
  DistribucionView,
  ProductosView,
  ResumenView,
} from "./Margenes/MargenesViews";
import { StoreMarginDrillPanel } from "./Margenes/StoreMarginDrillPanel";

type MargenesView = "resumen" | "distribucion" | "productos" | "criticos";

interface FiltersState {
  storeName: string | null;
  dateRange: DateRange;
}

export default function MarginsLevel1(): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // PRESERVAR: validación de permisos al inicio.
  const permissions = useMarginsPermissions();

  // Switch de accent CSS — /margenes usa naranja.
  useEffect(() => {
    const previous = document.documentElement.dataset["accent"];
    document.documentElement.dataset["accent"] = "orange";
    return () => {
      if (previous) document.documentElement.dataset["accent"] = previous;
      else delete document.documentElement.dataset["accent"];
    };
  }, []);

  // period NO se persiste: /margenes arranca SIEMPRE en el mes en curso
  // (defaultPeriod="mtd") en cada montaje. El cambio de período vive solo durante
  // la visita (sobrevive el switch de tabs internas; no sobrevive salir y volver).
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod());
  // compare y view sí se persisten (preferencias ortogonales al período).
  const [compare, setCompare] = useLocalStorageState<CompareKey>(
    "villapps:margenes:compare",
    "ly",
  );
  const [view, setView] = useLocalStorageState<MargenesView>(
    "villapps:margenes:view",
    "resumen",
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [drillItem, setDrillItem] = useState<StoreMarginMetrics | null>(null);

  // Dialog de detalle del producto (mismo que /compras/forecast).
  const {
    dialogState: productDialogState,
    openProductDetail,
    closeProductDetail,
    resolveError,
    clearResolveError,
  } = useProductDetailDialog();

  // Rango inicial coherente con el período por default (mes en curso). period
  // puede ser "custom" tras una edición manual → usa el default coherente.
  const initialPeriodRange = useMemo(
    () => periodToDateRange(period === "custom" ? defaultPeriod() : period),
    [period],
  );
  // filters tampoco se persiste: arranca limpio en el mes en curso cada visita.
  const [filters, setFilters] = useState<FiltersState>({
    storeName: null,
    dateRange: initialPeriodRange,
  });

  // F1: la sincronización period ↔ dateRange la hacen handlers explícitos
  // (handlePeriodChange / handleDateRangeChange), NO un useEffect frágil que
  // clobbereaba el override manual y dejaba el chip mintiendo.

  const [selectedMonth, setSelectedMonth] = useLocalStorageState<string | null>(
    "villapps:margenes:selectedMonth",
    null,
  );

  if (!permissions.isLoading && !permissions.canViewDashboard) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Alert severity="warning" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Acceso Restringido
          </Typography>
          <Typography>
            No tienes permisos para acceder al dashboard de análisis de
            márgenes. Contacta a tu administrador para obtener los permisos
            necesarios.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const startMonth = isoToMonthKey(filters.dateRange.start);
  const endMonth = clampToCurrentMonth(isoToMonthKey(filters.dateRange.end));

  const query = useMarginsLevel1Metrics(
    !!permissions.canViewDashboard && !permissions.isLoading,
    { startMonth, endMonth, storeName: filters.storeName },
  );

  const data = query.data;

  // Margen por tienda del mismo período del año anterior → variación del chip.
  const prevMarginByStore = useStoreMarginGrowth(
    !!permissions.canViewDashboard && !permissions.isLoading,
    startMonth,
    endMonth,
    filters.storeName,
  );

  const storeOptions = useMemo<StoreOption[]>(() => {
    if (!data) return [];
    return (data.topStores ?? []).map((s) => ({
      id: s.storeName,
      label: s.storeName,
    }));
  }, [data]);

  const selectedStoreOption =
    storeOptions.find((o) => o.label === filters.storeName) ?? null;

  const handleStoreChange = (next: StoreOption | null): void => {
    setFilters((prev) => ({ ...prev, storeName: next?.label ?? null }));
  };

  // Clic en un chip de período: el chip manda el rango (fuente única).
  const handlePeriodChange = (next: PeriodKey): void => {
    setPeriod(next);
    setFilters((prev) => ({ ...prev, dateRange: periodToDateRange(next) }));
  };

  // Edición manual del rango: el período pasa a "custom" → ningún chip preset
  // queda resaltado (el chip ya no miente).
  const handleDateRangeChange = (next: DateRange): void => {
    setPeriod("custom");
    setFilters((prev) => ({ ...prev, dateRange: next }));
  };

  const handleClearAll = (): void => {
    setPeriod(defaultPeriod());
    setFilters({
      storeName: null,
      dateRange: periodToDateRange(defaultPeriod()),
    });
  };

  // El FilterBar muestra el rango SOLO cuando es manual ("custom"). Con un preset
  // activo va vacío y los pickers abren en el mes actual (referenceDate=hoy).
  const filterBarDateRange: DateRange =
    period === "custom" ? filters.dateRange : { start: null, end: null };

  if (permissions.isLoading || (query.isLoading && !query.data)) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 1 }} />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={160}
                sx={{ flex: 1, borderRadius: 1 }}
              />
            ))}
          </Stack>
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1 }} />
        </Stack>
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        <Alert severity="error">
          Error al cargar las métricas de márgenes. Verifique que el servidor
          backend esté ejecutándose.
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        <Alert severity="warning">No hay datos de márgenes disponibles.</Alert>
      </Box>
    );
  }

  const displayStores: StoreMarginMetrics[] = data.topStores ?? [];
  const displayProducts: TopMarginProduct[] = data.topProductsCurrentMonth ?? [];
  const criticalProducts: TopMarginProduct[] = displayProducts.filter(
    (p) =>
      (p.realMarginPercentage ?? p.marginPercentage) < 22 ||
      (p.realMarginPercentage ?? p.marginPercentage) < p.marginPercentage - 4,
  );

  const current = pickCurrent(data.monthlyData ?? []);
  const currentLabel = current
    ? `${MONTH_LABELS_FULL[monthOrder(current.month)] ?? current.month} ${current.year}`
    : "Sin datos";

  // Valores financieros AGREGADOS sobre el período filtrado (no solo el último
  // mes). Para 1 mes el agregado == ese mes; para YTD/3M/12M suma el rango.
  const agg = aggregatePeriodMargins(data.monthlyData ?? []);
  const multiMonth = agg.monthsCount > 1;
  const marginAmount = agg.marginAmount;
  const projectedMarginAmount = current?.realMarginAmount ?? marginAmount;
  const marginPct = agg.marginPct;
  const revenue = agg.revenue;
  const cost = agg.cost;
  const discounts = agg.discounts;
  const commissions = agg.commissions;
  const erosion = discounts + commissions;
  const erosionPctOfSales = revenue > 0 ? (erosion / revenue) * 100 : 0;

  // Subtitle: "del período" cuando el rango abarca varios meses; "del mes" en MTD.
  const subtitleParts: string[] = [];
  if (agg.monthsCount > 0) {
    subtitleParts.push(
      `Margen bruto del ${multiMonth ? "período" : "mes"}: ${MarginsService.formatCurrency(marginAmount)}`,
    );
    subtitleParts.push(`(${marginPct.toFixed(1)}%)`);
    // La proyección solo aplica a un único mes en curso, no a un rango.
    if (
      !multiMonth &&
      current?.isCurrentMonth &&
      current.daysElapsed !== undefined &&
      current.daysInMonth !== undefined &&
      current.daysElapsed < current.daysInMonth &&
      projectedMarginAmount !== marginAmount
    ) {
      subtitleParts.push(
        `· proyectado ${MarginsService.formatCurrency(projectedMarginAmount)}`,
      );
    }
  }

  // Ranking por margen %; el delta del chip = variación vs mismo período del año
  // anterior (puntos porcentuales), null si la tienda no tiene base previa.
  const opMarginRank = buildMarginRankItems(displayStores, prevMarginByStore);

  // Scatter data — convertir TopMarginProduct → ScatterProduct.
  const scatterProducts: ScatterProduct[] = displayProducts.map((p) => ({
    id: p.productId,
    name: MarginsService.extractProductName(p.productName),
    unitsSold: p.quantitySold,
    realMarginPct: p.realMarginPercentage ?? p.marginPercentage,
    totalMargin: p.realMarginAmount ?? p.marginAmount * p.quantitySold,
    marginPct: p.marginPercentage,
  }));

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Stack spacing={3}>
        <PageHead
          title="Márgenes"
          subtitle={subtitleParts.join(" ")}
          actions={
            <>
              <PeriodSelector value={period} onChange={handlePeriodChange} />
              <CompareSelector value={compare} onChange={setCompare} />
              <Box
                component="button"
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                aria-expanded={filtersOpen}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid var(--border)",
                  backgroundColor: filtersOpen
                    ? "var(--bg-2)"
                    : "var(--surface)",
                  color: "var(--ink-2)",
                  fontSize: "13px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": { backgroundColor: "var(--bg-2)" },
                }}
              >
                <FilterListIcon sx={{ fontSize: 14 }} />
                Filtrar
              </Box>
            </>
          }
        />

        <Collapse in={filtersOpen} unmountOnExit>
          <FilterBar
            stores={storeOptions}
            selectedStore={selectedStoreOption}
            dateRange={filterBarDateRange}
            onStoreChange={handleStoreChange}
            onDateRangeChange={handleDateRangeChange}
            onClear={handleClearAll}
            granularity="month"
            loading={query.isFetching}
          />
        </Collapse>

        {criticalProducts.length > 0 && (
          <AlertBanner
            severity="warn"
            actionLabel="Revisar ahora"
            onAction={() => setView("criticos")}
          >
            <Box component="strong">{criticalProducts.length} productos</Box>{" "}
            con margen real bajo el 22% por descuentos o comisiones — erosión
            del mes:{" "}
            <Box component="strong" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {MarginsService.formatCurrency(erosion)}
            </Box>{" "}
            <Box component="span" sx={{ opacity: 0.7 }}>
              ({erosionPctOfSales.toFixed(1)}% sobre ventas)
            </Box>
          </AlertBanner>
        )}

        <KPICardsMargenes
          monthlyData={data.monthlyData}
          topStores={data.topStores}
          loading={query.isFetching && !query.data}
        />

        <SubnavTabs<MargenesView>
          tabs={[
            { key: "resumen", label: "Resumen" },
            { key: "distribucion", label: "Distribución" },
            { key: "productos", label: "Top margen" },
            {
              key: "criticos",
              label: "Críticos",
              badge: criticalProducts.length || undefined,
            },
          ]}
          active={view}
          onChange={setView}
        />

        {view === "resumen" && (
          <ResumenView
            monthlyData={data.monthlyData ?? []}
            selectedMonth={selectedMonth}
            onMonthSelect={(key) =>
              setSelectedMonth((prev) => (prev === key ? null : key))
            }
            multiMonth={multiMonth}
            revenue={revenue}
            discounts={discounts}
            cost={cost}
            marginAmount={marginAmount}
            opMarginRank={opMarginRank}
            onDrillStore={setDrillItem}
            scatterProducts={scatterProducts}
          />
        )}

        {view === "distribucion" && (
          <DistribucionView scatterProducts={scatterProducts} />
        )}

        {view === "productos" && (
          <ProductosView
            stores={displayStores}
            products={displayProducts}
            onProductClick={openProductDetail}
            fetching={query.isFetching}
          />
        )}

        {view === "criticos" && (
          <CriticosView
            products={criticalProducts}
            onProductClick={openProductDetail}
          />
        )}
      </Stack>

      <StoreMarginDrillPanel
        item={drillItem}
        currentLabel={currentLabel}
        onClose={() => setDrillItem(null)}
      />

      <ProductDetailDrawer
        open={productDialogState.open}
        unifiedProductId={productDialogState.unifiedProductId}
        productName={productDialogState.productName}
        onClose={closeProductDetail}
      />

      <Snackbar
        open={!!resolveError}
        autoHideDuration={5000}
        onClose={clearResolveError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={clearResolveError}
          sx={{ width: "100%" }}
        >
          {resolveError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
